/**
 * Helper functions for ensuring required HTML fields exist per node type
 */
import { REQUIRED_HTML_FIELDS } from '../shared/constants/index'
import type { LegacyNodeKind } from '../shared/legacyNodes'

/**
 * Ensure all required HTML fields exist for a node type
 * Merges existing HTML with required defaults
 */
export function ensureRequiredHtmlFields(
  kind: LegacyNodeKind,
  existingHtml: Record<string, unknown>,
): Record<string, unknown> {
  const requiredFields = REQUIRED_HTML_FIELDS[kind]
  if (!requiredFields) {
    return existingHtml
  }

  const html = { ...existingHtml }
  const hasOwn = (key: string) =>
    Object.prototype.hasOwnProperty.call(existingHtml, key)
  const preserveLegacyLeaveVoicemailFields =
    kind === 'leave_voicemail' &&
    !hasOwn('soundClipId') &&
    (hasOwn('message_type') || hasOwn('sound_clip_id'))
  const preserveMissingLeaveVoicemailSoundClipId =
    preserveLegacyLeaveVoicemailFields &&
    !hasOwn('sound_clip_id') &&
    hasOwn('message_type')

  Object.entries(requiredFields).forEach(([key, defaultValue]) => {
    // CRITICAL: For children arrays, preserve existing structure if it already exists
    // This prevents overwriting children arrays that were explicitly set before ensureRequiredHtmlFields
    // Legacy spec requires children to start with [{}], which should be set in node-specific converters
    if (key === 'children' && key in html && Array.isArray(html[key])) {
      // Preserve existing children array - don't overwrite
      return
    }
    // Preserve intentionally omitted legacy sound_clip_id during round-trip.
    if (
      preserveMissingLeaveVoicemailSoundClipId &&
      key === 'sound_clip_id' &&
      !(key in html)
    ) {
      return
    }
    if (!(key in html)) {
      html[key] = defaultValue
    }
  })

  if (kind === 'leave_voicemail') {
    if (preserveLegacyLeaveVoicemailFields) {
      if (
        'message_type' in html &&
        html['message_type'] !== null &&
        html['message_type'] !== undefined
      ) {
        html['message_type'] = String(html['message_type'])
      }

      if ('sound_clip_id' in html) {
        const soundClipIdValue = html['sound_clip_id']
        if (soundClipIdValue !== null && soundClipIdValue !== undefined) {
          html['sound_clip_id'] = String(soundClipIdValue)
        } else if (
          !Object.prototype.hasOwnProperty.call(existingHtml, 'sound_clip_id')
        ) {
          delete html['sound_clip_id']
        }
      }

      if ('soundClipId' in html) {
        delete html['soundClipId']
      }

      return html
    }

    // Handle soundClipId from React Flow data (before it's converted to sound_clip_id)
    // Check both soundClipId (React Flow format) and sound_clip_id (legacy format)
    // CRITICAL: Prefer soundClipId (React Flow format) as source of truth
    const soundClipId = html['soundClipId'] ?? html['sound_clip_id']

    // Map React Flow soundClipId values to legacy message_type and sound_clip_id
    // CRITICAL: Legacy Admin saves sound_clip_id as string "null" (not JSON null)
    // This matches the actual saved JSON format from Legacy Admin
    if (
      soundClipId === 'defined_by_extension' ||
      soundClipId === 'undefined' ||
      !soundClipId
    ) {
      html['message_type'] = '0'
      html['sound_clip_id'] = 'null' // String "null" to match Legacy Admin format
    } else if (soundClipId === 'system_generated') {
      html['message_type'] = '-2'
      html['sound_clip_id'] = 'null' // String "null" to match Legacy Admin format
    } else if (soundClipId === 'no_greeting') {
      html['message_type'] = '-3'
      html['sound_clip_id'] = 'null' // String "null" to match Legacy Admin format
    } else if (soundClipId && soundClipId !== 'null' && soundClipId !== null) {
      // It's a sound clip ID (numeric string)
      // CRITICAL: When sound_clip_id is a valid ID, message_type MUST be "-1"
      html['message_type'] = '-1'
      html['sound_clip_id'] = String(soundClipId)
    } else {
      // Default fallback
      html['message_type'] = '0'
      html['sound_clip_id'] = 'null' // String "null" to match Legacy Admin format
    }

    // CRITICAL: Validation - Ensure message_type and sound_clip_id are always consistent
    // This handles edge cases where data might be inconsistent (e.g., from legacy saved JSON)
    if (html['message_type'] === '-1') {
      // When using sound clip, sound_clip_id must be a valid ID (not "null")
      const soundClipIdValue = html['sound_clip_id']
      if (
        !soundClipIdValue ||
        soundClipIdValue === 'null' ||
        soundClipIdValue === null
      ) {
        // Invalid: message_type is "-1" but sound_clip_id is invalid
        // Default to "defined_by_extension" (message_type: "0")
        console.warn(
          'leave_voicemail: message_type is "-1" but sound_clip_id is invalid. Defaulting to "0".',
        )
        html['message_type'] = '0'
        html['sound_clip_id'] = 'null' // String "null" to match Legacy Admin format
      }
    } else if (
      html['message_type'] === '0' ||
      html['message_type'] === '-2' ||
      html['message_type'] === '-3'
    ) {
      // When not using sound clip, sound_clip_id must be "null"
      const soundClipIdValue = html['sound_clip_id']
      if (
        soundClipIdValue &&
        soundClipIdValue !== 'null' &&
        soundClipIdValue !== null
      ) {
        // Invalid: message_type is not "-1" but sound_clip_id is a valid ID
        // This means we should be using a sound clip, so set message_type to "-1"
        console.warn(
          `leave_voicemail: message_type is "${html['message_type']}" but sound_clip_id is "${soundClipIdValue}". Setting message_type to "-1".`,
        )
        html['message_type'] = '-1'
        // Keep the sound_clip_id as is (it's already a valid ID)
      } else {
        // Ensure sound_clip_id is the string "null" (matches Legacy Admin format)
        html['sound_clip_id'] = 'null'
      }
    }

    // Ensure message_type is a string
    if (typeof html['message_type'] !== 'string') {
      html['message_type'] = String(html['message_type'] ?? '0')
    }
    if (preserveMissingLeaveVoicemailSoundClipId) {
      const soundClipIdValue = html['sound_clip_id']
      const hasConcreteSoundClip =
        soundClipIdValue !== undefined &&
        soundClipIdValue !== null &&
        soundClipIdValue !== 'null' &&
        String(soundClipIdValue).trim() !== ''
      if (!hasConcreteSoundClip) {
        delete html['sound_clip_id']
      } else {
        html['sound_clip_id'] = String(soundClipIdValue)
      }
    } else {
      // CRITICAL: Ensure sound_clip_id is always a string (either valid ID or "null")
      // Legacy Admin saves string "null" (not JSON null) - this matches the actual saved format
      if (typeof html['sound_clip_id'] !== 'string') {
        // Convert to string - if null/undefined, use "null" string
        html['sound_clip_id'] = html['sound_clip_id']
          ? String(html['sound_clip_id'])
          : 'null'
      }
      // Ensure any actual null values are converted to string "null"
      if (
        html['sound_clip_id'] === null ||
        html['sound_clip_id'] === undefined
      ) {
        html['sound_clip_id'] = 'null'
      }
    }

    // Remove soundClipId if it exists (it's React Flow format, not legacy)
    if ('soundClipId' in html) {
      delete html['soundClipId']
    }
  }

  if (kind === 'change_caller_id') {
    // Ensure new_caller_id_option defaults to 'six_char' if not set (matches Legacy auto-select behavior)
    // Legacy CAN save null (when no radio button is checked), so preserve null if explicitly set
    // Only default to 'six_char' if the field is missing or undefined (not if it's explicitly null)
    if (
      !('new_caller_id_option' in html) ||
      html['new_caller_id_option'] === undefined ||
      html['new_caller_id_option'] === 'undefined'
    ) {
      html['new_caller_id_option'] = 'six_char'
    }
    // Note: If html['new_caller_id_option'] is explicitly null, we preserve it for legacy conformity
    // Only convert mode if new_caller_id_option needs to be set
    // This ensures valid values like 'six_char', 'replace_caller' are preserved
    // CRITICAL: Legacy uses "six_char" for "Add up to 20 characters to the front", NOT "prefix"!
    if (
      'mode' in html &&
      (html['new_caller_id_option'] === 'six_char' || // Only update if it's still the default
        html['new_caller_id_option'] === 'undefined' ||
        html['new_caller_id_option'] === undefined)
    ) {
      const mode = html['mode']
      if (mode === 'replace') {
        html['new_caller_id_option'] = 'replace_caller'
      } else if (mode === 'six_char') {
        html['new_caller_id_option'] = 'six_char'
      } else if (mode === 'add_front' || mode === 'prefix') {
        // CRITICAL: Legacy uses "six_char" for "Add up to 20 characters to the front", NOT "prefix"!
        html['new_caller_id_option'] = 'six_char'
      }
    }
    // Always delete mode after conversion (if it exists)
    delete html['mode']
  }

  if (kind === 'sms') {
    if ('auto_reply' in html && typeof html['auto_reply'] === 'boolean') {
      // Legacy format uses numbers 0/1 for boolean fields
      html['auto_reply'] = html['auto_reply'] ? 1 : 0
    }
  }

  if (kind === 'external_number') {
    // Timeout fallback: if empty during save, default to "10" (per legacy behavior)
    // CRITICAL: Preserve null if explicitly set (for strict conformity when loading legacy data)
    // Only default to '10' when missing/undefined/empty, not when explicitly null
    if (
      !('timeout' in html) ||
      html['timeout'] === undefined ||
      String(html['timeout']).trim() === ''
    ) {
      html['timeout'] = '10'
    } else if (html['timeout'] !== null) {
      // Clamp timeout: min 5, max 300 (per legacy line 7804)
      const timeoutNum =
        typeof html['timeout'] === 'number'
          ? html['timeout']
          : parseInt(String(html['timeout']), 10)
      if (!isNaN(timeoutNum)) {
        const clamped = Math.max(5, Math.min(300, timeoutNum))
        html['timeout'] = String(clamped)
      }
    }
  }

  if (kind === 'simple_dial') {
    // Clamp timeout: min 5, max 300 (per legacy line 7673)
    if (
      'timeout' in html &&
      html['timeout'] !== null &&
      html['timeout'] !== undefined
    ) {
      const timeoutNum =
        typeof html['timeout'] === 'number'
          ? html['timeout']
          : parseInt(String(html['timeout']), 10)
      if (!isNaN(timeoutNum)) {
        const clamped = Math.max(5, Math.min(300, timeoutNum))
        html['timeout'] = String(clamped)
      }
    }
  }

  if (kind === 'auto_attendant') {
    // Clamp timeout: min 2, max 20 (per legacy line 7735)
    if (
      'timeout' in html &&
      html['timeout'] !== null &&
      html['timeout'] !== undefined
    ) {
      const timeoutNum =
        typeof html['timeout'] === 'number'
          ? html['timeout']
          : parseInt(String(html['timeout']), 10)
      if (!isNaN(timeoutNum)) {
        const clamped = Math.max(2, Math.min(20, timeoutNum))
        html['timeout'] = String(clamped)
      }
    }
    // Clamp repeat_times: min 0, max 5 (per legacy line 7743).
    // When repeat_times is missing/null/invalid, default to "0" to match
    // Admin2 form behavior and avoid legacy fallback values.
    if (
      !('repeat_times' in html) ||
      html['repeat_times'] === null ||
      html['repeat_times'] === undefined ||
      String(html['repeat_times']).trim() === ''
    ) {
      html['repeat_times'] = '0'
    } else {
      const repeatNum =
        typeof html['repeat_times'] === 'number'
          ? html['repeat_times']
          : parseInt(String(html['repeat_times']), 10)
      if (!isNaN(repeatNum)) {
        const clamped = Math.max(0, Math.min(5, repeatNum))
        html['repeat_times'] = String(clamped)
      } else {
        html['repeat_times'] = '0'
      }
    }
    // Clamp children[].press_id: min 0, max 9999 (per legacy line 7755)
    if ('children' in html && Array.isArray(html['children'])) {
      html['children'].forEach((child, index) => {
        if (index > 0 && child && typeof child === 'object') {
          const childObj = child as Record<string, unknown>
          if ('press_id' in childObj && childObj['press_id'] !== undefined) {
            const pressIdNum =
              typeof childObj['press_id'] === 'number'
                ? childObj['press_id']
                : parseInt(String(childObj['press_id']), 10)
            if (!isNaN(pressIdNum)) {
              const clamped = Math.max(0, Math.min(9999, pressIdNum))
              childObj['press_id'] = String(clamped)
            }
          }
        }
      })
    }
  }

  if (kind === 'call_distribution') {
    // Clamp timeout: min 2, max 40 (per legacy line 7712)
    if (
      'timeout' in html &&
      html['timeout'] !== null &&
      html['timeout'] !== undefined
    ) {
      const timeoutNum =
        typeof html['timeout'] === 'number'
          ? html['timeout']
          : parseInt(String(html['timeout']), 10)
      if (!isNaN(timeoutNum)) {
        const clamped = Math.max(2, Math.min(40, timeoutNum))
        html['timeout'] = String(clamped)
      }
    }
    // Clamp children[].weight_id: min 1, max 100 (per legacy line 7721)
    if ('children' in html && Array.isArray(html['children'])) {
      html['children'].forEach((child, index) => {
        if (index > 0 && child && typeof child === 'object') {
          const childObj = child as Record<string, unknown>
          if ('weight_id' in childObj && childObj['weight_id'] !== undefined) {
            const weightIdNum =
              typeof childObj['weight_id'] === 'number'
                ? childObj['weight_id']
                : parseInt(String(childObj['weight_id']), 10)
            if (!isNaN(weightIdNum)) {
              const clamped = Math.max(1, Math.min(100, weightIdNum))
              childObj['weight_id'] = String(clamped)
            }
          }
        }
      })
    }
  }

  if (kind === 'transfer_rollover') {
    // Clamp timeout: min 5, max 600 (per legacy line 7863)
    if (
      'timeout' in html &&
      html['timeout'] !== null &&
      html['timeout'] !== undefined
    ) {
      const timeoutNum =
        typeof html['timeout'] === 'number'
          ? html['timeout']
          : parseInt(String(html['timeout']), 10)
      if (!isNaN(timeoutNum)) {
        const clamped = Math.max(5, Math.min(600, timeoutNum))
        html['timeout'] = String(clamped)
      }
    }
  }

  if (kind === 'interactive_webhook') {
    // Clamp children[].option_id: min 0, max 9999 (per legacy line 7785)
    if ('children' in html && Array.isArray(html['children'])) {
      html['children'].forEach((child, index) => {
        if (index > 0 && child && typeof child === 'object') {
          const childObj = child as Record<string, unknown>
          if ('option_id' in childObj && childObj['option_id'] !== undefined) {
            const optionIdNum =
              typeof childObj['option_id'] === 'number'
                ? childObj['option_id']
                : parseInt(String(childObj['option_id']), 10)
            if (!isNaN(optionIdNum)) {
              const clamped = Math.max(0, Math.min(9999, optionIdNum))
              childObj['option_id'] = String(clamped)
            }
          }
        }
      })
    }
  }

  if (kind === 'wait') {
    // Duration clamping: min 1, max 60 (per legacy line 7846)
    if (
      'duration' in html &&
      html['duration'] !== null &&
      html['duration'] !== undefined
    ) {
      const durationNum =
        typeof html['duration'] === 'number'
          ? html['duration']
          : parseInt(String(html['duration']), 10)

      if (!isNaN(durationNum)) {
        // Clamp to 1-60 range (matches legacy behavior)
        const clamped = Math.max(1, Math.min(60, durationNum))
        html['duration'] = String(clamped)
      }
    }
  }

  // P1-4 Fix: CRITICAL - Ensure dial_name lookup_id is never the string "undefined"
  // This handles cases where lookupId was missing from nodeData
  // and convertNullToLegacyString converted it to string "undefined"
  // Legacy saves JSON null when no radio button is checked (line 7652), not string "undefined"
  if (kind === 'dial_name') {
    if (
      html['lookup_id'] === 'undefined' ||
      html['lookup_id'] === undefined ||
      (html['lookup_id'] !== null &&
        html['lookup_id'] !== 'extension_name' &&
        html['lookup_id'] !== 'username')
    ) {
      // If lookup_id is invalid (string "undefined" or unknown value), set to null
      // Legacy saves null when no radio button is checked (line 7652)
      html['lookup_id'] = null
    }
  }

  return html
}
